/* Teeinblue owns form validation, preview and cart submission. This component only
   reports readiness; it never submits a native product form or copies a price. */
if (!customElements.get('timond-personalization-status')) {
  customElements.define('timond-personalization-status', class extends HTMLElement {
    connectedCallback() {
      this.loading = this.querySelector('[data-loading]');
      this.failure = this.querySelector('[data-failure]');
      this.scope = this.closest('.product-details') || this.parentElement;
      this.observer = new MutationObserver(() => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.checkReady(); });
      });
      this.observer.observe(this.scope, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['class', 'disabled', 'aria-disabled', 'aria-busy'] });
      this.previewObserver = new MutationObserver(records => {
        if (this.previewFrame || !records.some(record => record.target.closest?.('.tee-dialog-gallery')
          || [...record.addedNodes].some(node => node.matches?.('.tee-dialog-gallery') || node.querySelector?.('.tee-dialog-gallery')))) return;
        this.previewFrame = requestAnimationFrame(() => { this.previewFrame = null; this.enhancePreviewCart(); });
      });
      this.previewObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-expanded'] });
      this.onKeydown = (event) => {
        const control = event.target.closest('.tee-quantity-handle, .tee-btn--upload');
        if (control && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          control.click();
        }
      };
      this.scope.addEventListener('keydown', this.onKeydown);
      this.onEditorKeydown = event => {
        const control = event.target.closest('.tee-dialog-image-placement .tee-dialog-close, .tee-dialog-image-placement .tee-image-placement-replace');
        if (control && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          control.click();
        }
      };
      document.addEventListener('keydown', this.onEditorKeydown);
      // Verified in the installed Teeinblue SDK: this event follows a successful app cart add.
      // Refresh the count and drawer without submitting again or emitting another add event.
      this.onCartAdded = async () => {
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
          if (!response.ok) return;
          const cart = await response.json();
          if (!Number.isInteger(cart.item_count)) return;
          await customElements.whenDefined('cart-icon');
          document.querySelectorAll('cart-icon').forEach(icon => icon.renderCartBubble(cart.item_count));
          const drawer = document.querySelector('theme-drawer#cart-drawer');
          if (drawer && this.isConnected) {
            const { sectionRenderer } = await import('@theme/section-renderer');
            const html = await sectionRenderer.renderSection('cart-drawer-section', {
              cache: false, mode: 'hydration', injectStylesheet: true,
            });
            await customElements.whenDefined('theme-drawer');
            if (html && this.isConnected && drawer.querySelector('.cart-form')) drawer.open();
          }
        } catch { /* Cart remains available through the app's success link. */ }
      };
      document.addEventListener('teeinblue-event-after-cart-added', this.onCartAdded);
      this.timer = setTimeout(() => {
        if (!this.checkReady()) {
          this.loading.hidden = true;
          this.failure.hidden = false;
        }
      }, 15000);
      this.checkReady();
    }
    checkReady() {
      this.watchGallerySize();
      const atc = this.scope.querySelector('.tee-form-wrapper .tee-btn--atc');
      if (!atc) return false;
      this.loading.hidden = true;
      this.failure.hidden = true;
      clearTimeout(this.timer);
      this.improveFormAccessibility();
      this.formatPrices();
      this.enhancePreviewCart();
      return true;
    }
    enhancePreviewCart() {
      const template = this.querySelector('template[data-preview-cart]');
      if (!template) return;
      document.querySelectorAll('.tee-dialog-gallery .vm--modal[aria-expanded="true"]').forEach(modal => {
        const footer = modal.querySelector('.tee-dcontent-footer');
        const close = footer?.querySelector('button.tee-btn');
        if (!footer || !close || !modal.querySelector('#tee-popup-gallery')) return;
        let button = footer.querySelector('.timond-preview-atc');
        if (!button) {
          button = template.content.firstElementChild.cloneNode(true);
          button.addEventListener('click', () => this.addFromPreview(modal, button));
          footer.append(button);
        }
        const original = this.scope.querySelector('.tee-form-wrapper .tee-btn--atc');
        const disabled = this.previewCooldown || this.previewCartDisabled(original);
        if (button.disabled !== !!disabled) button.disabled = !!disabled;
      });
    }
    previewCartDisabled(button) {
      return !button || !button.isConnected || !button.getClientRects().length || button.disabled
        || button.getAttribute('aria-disabled') === 'true' || button.getAttribute('aria-busy') === 'true';
    }
    addFromPreview(modal, button) {
      const original = this.scope.querySelector('.tee-form-wrapper .tee-btn--atc');
      const close = modal.querySelector('.tee-dcontent-footer button.tee-btn');
      if (!this.isConnected || !modal.isConnected || modal.getAttribute('aria-expanded') !== 'true'
        || !close || this.previewCooldown || this.previewCartDisabled(original)) return;
      this.previewCooldown = true;
      button.disabled = true;
      close.click();
      original.click();
      this.previewCooldownTimer = setTimeout(() => { this.previewCooldown = false; this.enhancePreviewCart(); }, 750);
    }
    watchGallerySize() {
      const gallery = this.scope.closest('.shopify-section')?.querySelector('.tee-gallery-content');
      if (!gallery || gallery === this.galleryElement || typeof ResizeObserver === 'undefined') return;
      this.galleryObserver?.disconnect();
      cancelAnimationFrame(this.galleryFrame);
      this.galleryElement = gallery;
      this.galleryWidth = 0;
      this.galleryObserver = new ResizeObserver(() => {
        const width = gallery.clientWidth;
        if (width <= 0 || width === this.galleryWidth) return;
        this.galleryWidth = width;
        cancelAnimationFrame(this.galleryFrame);
        this.galleryFrame = requestAnimationFrame(() => {
          this.galleryFrame = null;
          const slider = gallery.querySelector('.tee-slider');
          if (this.isConnected && gallery.isConnected && slider
            && Math.abs(slider.offsetWidth - gallery.clientWidth) > 1) {
            window.dispatchEvent(new Event('resize'));
          }
        });
      });
      this.galleryObserver.observe(gallery);
    }
    formatPrices() {
      this.scope.querySelectorAll('.tee-product-price').forEach(price => {
        const walker = document.createTreeWalker(price, NodeFilter.SHOW_TEXT);
        let node;
        while ((node = walker.nextNode())) {
          const formatted = node.nodeValue.replace(/€\s*(-?\d[\d.,]*)/g, '$1\u00a0€');
          if (formatted !== node.nodeValue) node.nodeValue = formatted;
        }
      });
    }
    improveFormAccessibility() {
      const form = this.scope.querySelector('.tee-form-wrapper');
      if (!form) return;
      this.alignPurchaseActions(form);
      this.placePriceNote(form);
      const german = document.documentElement.lang.startsWith('de');
      form.querySelectorAll('.tee-field').forEach(field => {
        const input = field.querySelector('input:not([type=hidden]):not([type=radio]):not([type=checkbox]),textarea,select');
        const label = field.querySelector('.tee-field__heading > span:first-child');
        if (!input || !input.id || !label) return;
        label.id = `${input.id}-label`;
        input.setAttribute('aria-labelledby', label.id);
        input.setAttribute('aria-invalid', String(field.classList.contains('tee-field--invalid') || Boolean(field.querySelector('.tee-photo-error'))));
        const descriptions = [];
        field.querySelectorAll('.tee-input-hint,.tee-upload-instruction,.tee-required-message,.tee-photo-error,.tee-field__total-characters').forEach((node,index) => {
          node.id = `${input.id}-description-${index}`;
          descriptions.push(node.id);
          if (german && node.classList.contains('tee-input-hint') && node.textContent.includes('Bitte prüfen Sie die Vorschau')) {
            node.textContent = 'Prüfe die Schreibweise deines Namens sorgfältig in der Vorschau.';
          }
        });
        input.setAttribute('aria-describedby', descriptions.join(' '));
      });
      form.querySelectorAll('.tee-option').forEach((option,index) => {
        const label = option.querySelector('.tee-option__title');
        const group = option.querySelector('[role=radiogroup]');
        if (label && group) {
          label.id = `timond-option-${index}`;
          group.setAttribute('aria-labelledby', label.id);
        }
      });
      const quantity = form.querySelector('.tee-quantity-input');
      if (quantity) quantity.setAttribute('aria-label', german ? 'Anzahl' : 'Quantity');
      if (german) {
        form.querySelectorAll('.tee-btn--upload').forEach(node => {
          node.setAttribute('aria-label', node.textContent.includes('Anderes') ? 'Anderes Foto auswählen' : 'Foto auswählen');
          node.setAttribute('role', 'button');
        });
        const quantityLabel = form.querySelector('.tee-quantity .tee-field__title');
        if (quantityLabel && quantityLabel.textContent !== 'Anzahl') quantityLabel.textContent = 'Anzahl';
        const gallery = document.querySelector('.tee-gallery-wrapper');
        gallery?.querySelectorAll('.tee-thumbnails__button--prev,.tee-slider__button--prev').forEach(node => node.setAttribute('aria-label', 'Vorheriges Bild'));
        gallery?.querySelectorAll('.tee-thumbnails__button--next,.tee-slider__button--next').forEach(node => node.setAttribute('aria-label', 'Nächstes Bild'));
      }
      form.querySelectorAll('.tee-quantity-handle').forEach(control => {
        control.setAttribute('role','button');
        control.tabIndex = 0;
        const minus = control.classList.contains('tee-quantity-minus');
        control.setAttribute('aria-label', german ? (minus ? 'Anzahl verringern' : 'Anzahl erhöhen') : (minus ? 'Decrease quantity' : 'Increase quantity'));
      });
      if (german) this.enhancePhotos(form);
    }
    placePriceNote(form) {
      // Keep server-rendered market tax text next to the app-owned price.
      // Retain the node so a campaign re-render can reattach it without duplicates.
      this.priceNote ||= this.querySelector('[data-price-note]');
      const price = form.querySelector('.tee-product-price');
      if (price && this.priceNote && price.nextElementSibling !== this.priceNote) {
        price.after(this.priceNote);
      }
      this.deliveryEstimate ||= this.scope.querySelector('timond-delivery-estimate');
      if (price && this.priceNote && this.deliveryEstimate && this.priceNote.nextElementSibling !== this.deliveryEstimate) {
        this.priceNote.after(this.deliveryEstimate);
      }
    }
    alignPurchaseActions(form) {
      const actions = form.querySelector('.tee-form-actions');
      const atc = actions?.querySelector('.tee-form-atc-wrapper > .tee-btn--atc');
      const preview = actions?.querySelector('.tee-btn--preview')?.closest('.tee-form-action');
      // Move the existing preview node, retaining the app's click handler and
      // putting quantity -> preview -> cart in both visual and keyboard order.
      if (atc && preview && preview !== atc.parentElement && preview.nextElementSibling !== atc) {
        atc.parentElement.insertBefore(preview, atc);
      }
      // Keep the icon inside the app's label so its loading/disabled treatment
      // also applies to the decoration. Reuse Horizon's exact SVG asset.
      const label = atc?.querySelector(':scope > span');
      const icon = this.querySelector('template[data-cart-icon]');
      if (label && icon && !label.querySelector('.timond-cart-icon')) {
        label.prepend(icon.content.cloneNode(true));
      }
    }
    enhancePhotos(form) {
      const photoFields = form.querySelectorAll('.tee-field--photo');
      // One shared disclosure per form; retain its open state across app re-renders.
      const existingTips = [...form.querySelectorAll('.timond-photo-tips')];
      this.photoTips ||= existingTips[0];
      existingTips.forEach(tips => { if (tips !== this.photoTips) tips.remove(); });
      if (!photoFields.length) {
        this.photoTips?.remove();
        return;
      }
      if (!this.photoTips) {
        const tips = document.createElement('details');
        tips.className = 'timond-photo-tips';
        tips.innerHTML = '<summary>Fototipps</summary><ol><li>Wähle ein scharfes Originalfoto in hoher Auflösung. Das Motiv sollte gut beleuchtet und vollständig sichtbar sein.</li><li>Nach dem Upload kannst du den Bildausschnitt direkt am Geschenk prüfen, verschieben, vergrößern oder drehen. Eine Bildbearbeitung kann etwas Zeit benötigen.</li><li>Speichere deine Anpassung und prüfe anschließend die gesamte Gestaltung über „Vorschau“.</li></ol>';
        this.photoTips = tips;
      }
      const lastPhoto = photoFields[photoFields.length - 1];
      if (this.photoTips.parentElement !== lastPhoto) lastPhoto.append(this.photoTips);
      photoFields.forEach(field => {
        field.querySelectorAll('.tee-photo-head button').forEach(button => button.setAttribute('aria-label', 'Foto entfernen'));
        field.querySelectorAll('.tee-photo-head img').forEach(img => img.setAttribute('alt', 'Dein hochgeladenes Foto'));
      });
      if (!this.editorObserver) {
        // The app renders its editor in a body portal outside the product form.
        // Ignore unrelated store mutations and leave all app handlers/data intact.
        this.editorObserver = new MutationObserver(records => {
          if (this.editorFrame || !records.some(record =>
            record.target.closest?.('.tee-dialog-image-placement') ||
            [...record.addedNodes].some(node => node.matches?.('.tee-dialog-image-placement') || node.querySelector?.('.tee-dialog-image-placement'))
          )) return;
          this.editorFrame = requestAnimationFrame(() => { this.editorFrame = null; this.enhancePhotoEditor(); });
        });
        this.editorObserver.observe(document.body, { childList: true, subtree: true });
      }
      this.enhancePhotoEditor();
    }
    enhancePhotoEditor() {
      document.querySelectorAll('.tee-dialog-image-placement').forEach(editor => {
        const inner = editor.querySelector('.tee-dialog-inner');
        if (!inner) return;
        let heading = inner.querySelector('.timond-photo-editor-heading');
        if (!heading) {
          heading = document.createElement('h2');
          heading.className = 'timond-photo-editor-heading';
          heading.id = 'timond-photo-editor-title';
          heading.textContent = 'Foto anpassen';
          inner.prepend(heading);
        }
        editor.querySelector('[role=dialog]')?.setAttribute('aria-labelledby', heading.id);
        const close = editor.querySelector('.tee-dialog-close');
        if (close) {
          close.setAttribute('role', 'button');
          close.tabIndex = 0;
          close.setAttribute('aria-label', 'Bildbearbeitung schließen');
        }
        const controls = editor.querySelectorAll('.tee-toolbar > button');
        // Verified eight-control layout from the installed Teeinblue editor.
        if (controls.length === 8) {
          const labels = ['Verkleinern', 'Vergrößern', 'Nach links drehen', 'Nach rechts drehen', 'Nach links verschieben', 'Nach rechts verschieben', 'Nach oben verschieben', 'Nach unten verschieben'];
          controls.forEach((button, index) => { button.setAttribute('aria-label', labels[index]); button.setAttribute('title', labels[index]); });
        }
        editor.querySelector('.tee-image-placement-replace')?.setAttribute('role', 'button');
      });
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.editorObserver?.disconnect();
      this.previewObserver?.disconnect();
      cancelAnimationFrame(this.previewFrame);
      clearTimeout(this.previewCooldownTimer);
      this.previewCooldown = false;
      this.galleryObserver?.disconnect();
      cancelAnimationFrame(this.galleryFrame);
      this.galleryElement = null;
      cancelAnimationFrame(this.editorFrame);
      this.scope?.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('keydown', this.onEditorKeydown);
      document.removeEventListener('teeinblue-event-after-cart-added', this.onCartAdded);
    }
  });
}
